import React from "react";
import { motion } from "framer-motion";
import { Briefcase, Sparkles, ClipboardCheck, Wrench, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const SERVICE_ICONS = {
  office: Briefcase,
  deep: Sparkles,
  standard: ClipboardCheck,
  caretaker: Wrench,
};

const SERVICE_IMAGES = {
  office: "https://customer-assets.emergentagent.com/job_facility-experts-12/artifacts/3duxl59z_zemka%203.webp",
  deep: "https://customer-assets.emergentagent.com/job_facility-experts-12/artifacts/u7xc9x96_zemka%204.webp",
  standard: "https://customer-assets.emergentagent.com/job_facility-experts-12/artifacts/szy5ier1_zemka%205.webp",
  caretaker: "https://customer-assets.emergentagent.com/job_facility-experts-12/artifacts/rodgb98e_zemka%201.webp",
};

export const Services = () => {
  const { t } = useLanguage();
  return (
    <section id="services" data-testid="services-section" className="bg-[#F4F7F9] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-600" data-testid="services-eyebrow">
            {t.services.eyebrow}
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
            {t.services.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{t.services.subtitle}</p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {t.services.items.map((item, i) => {
            const Icon = SERVICE_ICONS[item.key];
            return (
              <motion.article
                key={item.key}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="bg-white border border-slate-200 hover:border-cyan-500 hover:-translate-y-2 hover:shadow-2xl shadow-sm transition-all duration-500 flex flex-col h-full group"
                data-testid={`service-card-${item.key}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-slate-200">
                  <img
                    src={SERVICE_IMAGES[item.key]}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-[#0F172A] text-cyan-400 flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                </div>
                <div className="p-7 lg:p-8 flex-1 flex flex-col">
                  <h3 className="font-display text-xl lg:text-2xl font-bold text-slate-900 leading-tight">{item.title}</h3>
                  <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{item.desc}</p>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {item.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 size={16} className="mt-0.5 text-cyan-500 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:text-cyan-600 transition-colors w-fit"
                    data-testid={`service-cta-${item.key}`}
                  >
                    {t.services.learnMore}
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
